import { useState } from 'react';
import { useTranslations } from 'app/hooks/useTranslations';
import AuthenticationService from 'app/services/authentication';
import ConfigService from 'app/services/configService';
import { useTranslationStore } from 'app/stores/translationStore';

export const usePasswordRecovery = () => {
  const { fetchTranslations, setLang } = useTranslations();
  const currentLang = useTranslationStore(state => state.currentLang);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [hasError, setHasError] = useState(false);

  const authenticationService = new AuthenticationService(
    process.env.NEXT_PUBLIC_API_BASE_URL ?? ''
  );

  const loadPublicTranslations = async (): Promise<void> => {
    const configService = new ConfigService(
      process.env.NEXT_PUBLIC_API_BASE_URL ?? ''
    );
    let availableLangs: string[] = [];
    try {
      availableLangs = await configService.getLanguages();
    } catch {
      availableLangs = [currentLang];
    }

    const preferredLang = availableLangs.includes(currentLang)
      ? currentLang
      : (availableLangs[0] ?? currentLang);

    setLang(preferredLang);
    const loaded = await fetchTranslations(preferredLang);
    if (loaded) return;

    const fallbacks = availableLangs.filter(lang => lang !== preferredLang);
    for (const fallback of fallbacks) {
      const fallbackLoaded = await fetchTranslations(fallback);
      if (fallbackLoaded) {
        setLang(fallback);
        break;
      }
    }
  };

  const requestReset = async (email: string): Promise<void> => {
    setIsLoading(true);
    setHasError(false);
    try {
      await authenticationService.requestPasswordReset(email);
      setIsSuccess(true);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (
    token: string,
    newPassword: string
  ): Promise<boolean> => {
    setIsLoading(true);
    setHasError(false);
    try {
      const success = await authenticationService.resetPassword(
        token,
        newPassword
      );
      setIsSuccess(success);
      setHasError(!success);
      return success;
    } catch {
      setHasError(true);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    isSuccess,
    hasError,
    requestReset,
    resetPassword,
    loadPublicTranslations,
  };
};
